import { useQuery } from '@apollo/client/react';
import {
  BodyText,
  Link,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@utilitywarehouse/hearth-react';
import { ChevronDownMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import { useCallback, useEffect } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { ProfileInformation } from '@generated/gql/graphql';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { ClientType, useAuthContext } from '@services/Auth';
import { getLogoutUrl } from '@services/Auth/utils';
import { AssistantType } from '@src/types/attribution';
import { clearFormPersistStorage } from '@utils/storagePropertyManagement';

import { PartnerProfileShortInformation } from '../Partner/query';
import './styles.scss';

const getFeedbackUrl = (positionId: string, basketId: string) => {
  const url = new URL(EXTERNAL_LINKS.partnerFeedback);
  url.searchParams.append('entry.1553896436', positionId);
  url.searchParams.append('entry.136403437', basketId);
  return url.toString();
};

const formatName = (profileInformation: ProfileInformation) => {
  switch (profileInformation.__typename) {
    case 'PartnerProfileInformation':
      return (
        profileInformation.name ||
        profileInformation.handle ||
        profileInformation.partnerPositionId
      );
    case 'SalesProfileInformation':
      return profileInformation.email?.length
        ? profileInformation.email.split('@')[0]
        : null;
  }

  return null;
};

// Presentational component - pure UI, no hooks
interface ProfileUIProps {
  profileName: string | null;
  isPartner: boolean;
  showPartnerFeedback: boolean;
  showStartAgain: boolean;
  showLogout: boolean;
  isHosted: boolean;
  onFeedback: () => void;
  onStartAgain: () => void;
  onLogout: () => void;
}

const ProfileUI = ({
  profileName,
  isPartner,
  showPartnerFeedback,
  showStartAgain,
  showLogout,
  isHosted,
  onFeedback,
  onStartAgain,
  onLogout,
}: ProfileUIProps) => {
  if (!profileName) return null;

  if (isHosted) {
    return (
      <BodyText color="inverted" size="md" weight="semibold">
        {profileName}
      </BodyText>
    );
  }

  return (
    <Menu>
      <Link
        asChild
        className="profile__button"
        inverted
        aria-controls="profile-dropdown"
        aria-haspopup="true"
      >
        <MenuTrigger>
          <button type="button">
            <span>{profileName}</span>
            <ChevronDownMediumIcon />
          </button>
        </MenuTrigger>
      </Link>

      <MenuContent id="profile-dropdown" placement="bottomRight">
        {isPartner && showPartnerFeedback && (
          <MenuItem onClick={onFeedback}>Give feedback</MenuItem>
        )}
        {showStartAgain && (
          <MenuItem onClick={onStartAgain}>Start a new quote</MenuItem>
        )}
        {showLogout && <MenuItem onClick={onLogout}>Log out</MenuItem>}
      </MenuContent>
    </Menu>
  );
};

interface ProfileProps {
  attributionToken?: string;
  hostedId?: string;
  clearAttribution?: () => void;
}

const Profile = ({
  attributionToken,
  hostedId,
  clearAttribution,
}: ProfileProps) => {
  const userInformation = useUserInformation();
  const { basketId, handleStartAgain: resetBasket } = useBasketContext();
  const { resetDetails } = useDetailsContext();
  const { endAppointment, appointment } = useAppointmentContext();

  const { activeClient } = useAuthContext();
  const setSnackbarError = useSnackbarError();
  const isHosted = Boolean(hostedId);
  const startAgainDialog = useDialog(DialogID.START_AGAIN);

  const {
    data,
    loading,
    error: profileError,
  } = useQuery(PartnerProfileShortInformation, {
    skip: !isHosted || !!appointment || !!userInformation,
    variables: { positionId: hostedId ?? '' },
  });

  useEffect(() => {
    if (profileError) {
      setSnackbarError(new Error('Could not find partner profile'));
      clearAttribution?.();
    }
  }, [profileError, setSnackbarError, clearAttribution]);

  const handleLogout = useCallback(
    async ({ removeHostedAttribution = true } = {}) => {
      if (appointment) {
        await endAppointment(true, { removeHostedAttribution });
      } else {
        resetBasket({ removeHostedAttribution });
        clearFormPersistStorage();
        resetDetails();
      }
      window.location.href = getLogoutUrl();
    },
    [appointment, endAppointment, resetBasket, resetDetails]
  );

  useEffect(() => {
    if (attributionToken && userInformation) {
      handleLogout({ removeHostedAttribution: false });
    }
  }, [attributionToken, userInformation, handleLogout]);

  const isPartner = userInformation?.type === AssistantType.PARTNER;
  const profileName = isHosted
    ? data?.profileShortInfo?.name || hostedId
    : userInformation
      ? formatName(userInformation)
      : null;

  const handleFeedback = () => {
    const positionId = isPartner ? userInformation?.partnerPositionId : '';
    const url = getFeedbackUrl(positionId ?? '', basketId);
    window.open(url, '_blank');
  };

  const handleStartAgain = () => {
    startAgainDialog.setOpen(true);
  };

  if (isHosted && loading) {
    return null;
  }

  return (
    <ProfileUI
      profileName={profileName ?? null}
      isPartner={isPartner}
      showPartnerFeedback={!!profileName}
      showStartAgain={!!profileName}
      showLogout={activeClient !== ClientType.PartnerApp}
      onFeedback={handleFeedback}
      onStartAgain={handleStartAgain}
      onLogout={handleLogout}
      isHosted={isHosted}
    />
  );
};

export { Profile, ProfileUI, formatName };
export type { ProfileUIProps, ProfileProps };
